import { create } from 'zustand';
import { getApiEndpoints } from '@business-assistant/shared/config';

// LiveKit routed through backend gateway
const getStreamBase = () => getApiEndpoints().livekit;

// =============================================================================
// TYPES
// =============================================================================

export type MeetingStatus = 'scheduled' | 'active' | 'ended';

export interface VideoCallMeeting {
  id: string;
  name: string;
  slug: string;
  title: string;
  guestAccessEnabled: boolean;
  createdBy: string;
  createdAt: string;
  scheduledAt: string | null;
  duration: number | null;
  participants: string[];
  status: MeetingStatus;
  guestLink?: string;
}

export interface MeetingContext {
  status: MeetingStatus;
  scheduledAt: string | null;
  isOrganizer: boolean;
  title: string;
}

interface CreateRoomParams {
  name: string;
  guestAccessEnabled?: boolean;
}

interface CreateMeetingParams {
  title: string;
  scheduledAt?: string | null;
  duration?: number | null;
  participants?: string[];
  guestAccessEnabled?: boolean;
}

interface VideoCallState {
  currentRoomId: string | null;
  token: string | null;
  wsUrl: string | null;
  isInCall: boolean;
  isConnecting: boolean;
  error: string | null;
  meetings: VideoCallMeeting[];
  meetingsLoading: boolean;

  joinRoom: (roomName: string) => Promise<void>;
  leaveRoom: () => void;
  createRoom: (params: CreateRoomParams) => Promise<VideoCallMeeting>;
  generateGuestLink: (roomId: string) => Promise<string>;
  createMeeting: (params: CreateMeetingParams) => Promise<VideoCallMeeting>;
  getMeetings: () => Promise<void>;
  startMeeting: (slug: string) => Promise<void>;
  getMeetingBySlug: (slug: string) => VideoCallMeeting | undefined;
  getMeetingStatus: (slug: string) => Promise<MeetingContext>;
}

// =============================================================================
// AUTH HELPERS
// =============================================================================

function getAuthToken(): string | null {
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.accessToken || parsed.token || null;
    }
  } catch {
    // fallback
  }
  return null;
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// STORE
// =============================================================================

export const useVideoCallStore = create<VideoCallState>((set, get) => ({
  currentRoomId: null,
  token: null,
  wsUrl: null,
  isInCall: false,
  isConnecting: false,
  error: null,
  meetings: [],
  meetingsLoading: false,

  joinRoom: async (roomName: string) => {
    set({ isConnecting: true, error: null });
    try {
      const response = await fetch(`${getStreamBase()}/token`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ roomName }),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to get token');
      }

      set({
        currentRoomId: roomName,
        token: data.token,
        wsUrl: data.wsUrl,
        isInCall: true,
        isConnecting: false,
      });
    } catch (err) {
      set({
        isConnecting: false,
        error: err instanceof Error ? err.message : 'Failed to join room',
      });
      throw err;
    }
  },

  leaveRoom: () => {
    set({
      currentRoomId: null,
      token: null,
      wsUrl: null,
      isInCall: false,
      isConnecting: false,
      error: null,
    });
  },

  createRoom: async (params: CreateRoomParams): Promise<VideoCallMeeting> => {
    const response = await fetch(`${getStreamBase()}/rooms`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(params),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create room');
    }

    const meeting: VideoCallMeeting = {
      id: `vcl-${data.room.slug}`,
      ...data.room,
      guestLink: data.guestLink,
    };

    set((state) => ({ meetings: [meeting, ...state.meetings] }));
    return meeting;
  },

  generateGuestLink: async (roomId: string): Promise<string> => {
    return `${window.location.origin}/meet/${roomId}`;
  },

  createMeeting: async (params: CreateMeetingParams): Promise<VideoCallMeeting> => {
    const roomName = `meeting-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const response = await fetch(`${getStreamBase()}/rooms`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        name: roomName,
        guestAccessEnabled: params.guestAccessEnabled ?? true,
        title: params.title,
        scheduledAt: params.scheduledAt,
        duration: params.duration,
        participants: params.participants,
      }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create meeting');
    }

    const meeting: VideoCallMeeting = {
      id: `vcl-${data.room.slug}`,
      ...data.room,
      guestLink: data.guestLink,
    };

    set((state) => ({ meetings: [meeting, ...state.meetings] }));
    return meeting;
  },

  getMeetings: async () => {
    set({ meetingsLoading: true });
    try {
      const response = await fetch(`${getStreamBase()}/meetings`, {
        headers: authHeaders(),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to list meetings');
      }

      const meetings: VideoCallMeeting[] = data.meetings.map((m: any) => ({
        id: `vcl-${m.slug}`,
        ...m,
      }));

      set({ meetings, meetingsLoading: false });
    } catch (err) {
      set({ meetingsLoading: false });
      throw err;
    }
  },

  startMeeting: async (slug: string) => {
    const response = await fetch(`${getStreamBase()}/rooms/${slug}/start`, {
      method: 'POST',
      headers: authHeaders(),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to start meeting');
    }

    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.slug === slug ? { ...m, status: 'active' as MeetingStatus } : m,
      ),
    }));
  },

  getMeetingBySlug: (slug: string) => {
    return get().meetings.find((m) => m.slug === slug);
  },

  getMeetingStatus: async (slug: string): Promise<MeetingContext> => {
    const response = await fetch(`${getStreamBase()}/rooms/${slug}/status`, {
      headers: { 'Content-Type': 'application/json' },
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get meeting status');
    }

    return data.meeting;
  },
}));
